import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Space, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoading } from '../../hooks/useLoading';

const { Title, Text } = Typography;

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

/**
 * 로그인 폼 컴포넌트
 * 
 * 이 컴포넌트는 사용자 로그인 기능을 제공합니다.
 * 일반 로그인과 Google OAuth 로그인을 지원합니다.
 * 
 * 주요 기능:
 * - 사용자명/비밀번호 로그인
 * - Google OAuth 로그인
 * - 로그인 상태 관리
 * - 에러 처리 및 메시지 표시
 * 
 * @component
 */
const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, setLoading } = useLoading();
  const [form] = Form.useForm();

  // 언어 변경 시 기존 메시지 제거
  useEffect(() => {
    message.destroy();
  }, [i18n.language]);

  // 동적 번역 메시지 생성 함수
  const getValidationMessage = (type: 'required' | 'minLength' | 'maxLength', field: string, value?: number) => {
    const fieldName = t(`validation.${field}`);
    
    switch (type) {
      case 'required':
        return t('validation.required', { field: fieldName });
      case 'minLength':
        return t('validation.minLength', { field: fieldName, min: value });
      case 'maxLength':
        return t('validation.maxLength', { field: fieldName, max: value });
      default:
        return '';
    }
  };

  const handleSubmit = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      await login(values.username, values.password);
      message.success(t('auth.loginSuccess'));
      
      // 리다이렉트 URL이 있으면 해당 페이지로, 없으면 기본 페이지로 이동
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(decodeURIComponent(redirectTo));
      } else {
        navigate('/laws');
      }
    } catch (error: any) {
      message.error(t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            {t('auth.login')}
          </Title>
          <Text type="secondary">
            {t('auth.welcomeMessage')}
          </Text>
        </div>

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: getValidationMessage('required', 'username') },
              { min: 3, message: getValidationMessage('minLength', 'username', 3) },
              { max: 50, message: getValidationMessage('maxLength', 'username', 50) }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('auth.username')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: getValidationMessage('required', 'password') },
              { min: 6, message: getValidationMessage('minLength', 'password', 6) }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.password')}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{ width: '100%' }}
            >
              {t('auth.login')}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space>
              <Text type="secondary">{t('auth.noAccount')}</Text>
              <Button type="link" onClick={onSwitchToRegister} style={{ padding: 0 }}>
                {t('auth.register')}
              </Button>
            </Space>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <Button 
                type="text" 
                onClick={() => navigate('/')} 
                style={{ color: '#666', fontSize: '14px' }}
              >
                ← {t('auth.backToHome')}
              </Button>
            </div>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default LoginForm; 